/**
 * OnboardingPickerButton Component
 *
 * A button that displays the selected onboarding with version info,
 * and opens a picker dialog when clicked.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClearIcon from '@mui/icons-material/Clear';
import { OnboardingPickerDialog } from './OnboardingPickerDialog';
import type {
  OnboardingPickerButtonProps,
  OnboardingSelection,
} from './OnboardingPicker.types';

/**
 * Format date for display
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * OnboardingPickerButton Component
 *
 * @example
 * ```tsx
 * <OnboardingPickerButton
 *   selection={selection}
 *   onSelectionChange={setSelection}
 *   projectId="project-123"
 *   onViewClick={handleViewClick}
 * />
 * ```
 */
export const OnboardingPickerButton: React.FC<OnboardingPickerButtonProps> = ({
  selection,
  onSelectionChange,
  projectId,
  disabled = false,
  loading = false,
  onViewClick,
}) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);

  /**
   * Handle dialog open
   */
  const handleOpenDialog = useCallback(() => {
    if (!disabled) {
      setDialogOpen(true);
    }
  }, [disabled]);

  /**
   * Handle dialog close
   */
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  /**
   * Handle selection from dialog
   */
  const handleSelect = useCallback((newSelection: OnboardingSelection) => {
    onSelectionChange(newSelection);
    setDialogOpen(false);
  }, [onSelectionChange]);

  /**
   * Handle clear selection
   */
  const handleClear = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    onSelectionChange(null);
  }, [onSelectionChange]);

  /**
   * Handle view click
   */
  const handleViewClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (selection && onViewClick) {
      onViewClick(selection.onboardingId, selection.versionId);
    }
  }, [selection, onViewClick]);

  return (
    <>
      {/* Loading state */}
      {loading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            p: theme.spacing(1),
            minWidth: 180,
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      )}

      {/* No selection - show simple button */}
      {!loading && !selection && (
        <Button
          variant="outlined"
          onClick={handleOpenDialog}
          disabled={disabled}
          sx={{ minWidth: 180 }}
        >
          Select Onboarding
        </Button>
      )}

      {/* Has selection - show card with info */}
      {!loading && selection && (
        <Paper
          variant="outlined"
          sx={{
            p: theme.spacing(1),
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            minWidth: 250,
            '&:hover': disabled
              ? {}
              : {
                  borderColor: theme.palette.primary.main,
                },
          }}
          onClick={handleOpenDialog}
        >
          {/* Onboarding info */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              noWrap
              title={selection.onboardingName}
            >
              {selection.onboardingName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {selection.versionName} • {formatDate(selection.versionCreatedAt)}
            </Typography>
          </Box>

          {/* View button */}
          {onViewClick && (
            <Button
              size="small"
              variant="text"
              startIcon={<VisibilityIcon />}
              onClick={handleViewClick}
              disabled={disabled}
              sx={{ minWidth: 'auto', flexShrink: 0 }}
            >
              View
            </Button>
          )}

          {/* Clear button */}
          <IconButton
            size="small"
            aria-label="Clear selection"
            onClick={handleClear}
            disabled={disabled}
            sx={{ flexShrink: 0 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}

      {/* Picker Dialog */}
      <OnboardingPickerDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSelect={handleSelect}
        projectId={projectId}
        selectedOnboardingId={selection?.onboardingId}
      />
    </>
  );
};
